import React, { useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import ROUTES from '../../../Routes/constants';
import {
  TradeContextName,
  TradeState,
  TradeStateName,
  TradeViewVariant
} from '../../../../_shared/types/trade';
import {
  BidSource,
  BidStatus,
  BidStatusName
} from '../../../../_shared/types/auction';
import TradeDetails from '../../../../_shared/models/TradeDetails';
import Loading from '../../../../_shared/components/Loading';
import ReducerConstants from '../../../../_shared/constants/reducers';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import ActiveTimer from '../../../../_shared/components/ActiveTimer';
import convertToCurrency from '../../../../_shared/utils/convertToCurrency';
import NumberOfBids from './NumberOfBids';
import TopBid from './TopBid';
import BidBoxContext from '../../BidBoxContainer/BidBoxContext';
import useAnalytics, {
  getVinBasicProperties,
  getSellerProperties
} from '../../../../_shared/hooks/useAnalytics';

import {
  BidInfoDetails,
  BidInfoTop,
  BidInfoWrapper,
  StyledActiveTimer,
  StyledFloorPrice,
  StyledFloorPriceLabel,
  StyledFullHistoryLink,
  StyledSummary,
  StyledSummaryInfoWrapper
} from './Styles';
import isOmpPlatform from '../../../../_shared/utils/isOmpPlatform';

export const SUMMARY_VARIANT = {
  PENDING: 'pending',
  MODAL: 'modal',
  TIMER: 'timer',
  OVERVIEW: 'overview'
};

const Summary = ({
  isLoading,
  variant,
  bidContext,
  isSelling,
  manufacturers,
  testId,
  isAuctionExtended,
  tradeContext
}) => {
  const { tradeDetails } = useContext(BidBoxContext);
  const track = useAnalytics();
  const isOmp = isOmpPlatform();
  const {
    auctionSummary,
    bidStatus,
    id,
    numberOfBids,
    tradeFloorPrice,
    tradeState,
    tradeVehicleSummary,
    activeExpiryTime,
    vin,
    isTimerExtended
  } = bidContext ?? {};
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );

  const isCSU = useMemo(() => user?.id !== user?.superUserId, [
    user?.id,
    user?.superUserId
  ]);

  const trackViewHistory = () => {
    if (isOmp) {
      track(MixpanelConfig.names.viewBidHistory, {
        ...getVinBasicProperties(tradeDetails),
        ...getSellerProperties(tradeDetails)
      });
    } else {
      track(MixpanelConfig.names.viewBidHistory, {
        trade_id: id,
        auction_id: auctionSummary.id,
        year: tradeVehicleSummary?.vehicleSummary.modelYear,
        make: TradeDetails.getManufacturerName(
          tradeVehicleSummary?.vehicleSummary,
          manufacturers
        ),
        model: tradeVehicleSummary?.vehicleSummary.model.displayName,
        total_bids: numberOfBids,
        trade_state: TradeStateName[tradeState],
        trade_state_section: TradeContextName[tradeContext],
        vin,
        trim: tradeVehicleSummary?.vehicleSummary.trim,
        is_floor_met: tradeFloorPrice?.isFloorPriceMet ?? 'N/A',
        user_bid_status: bidStatus
          ? BidStatusName[bidStatus]
          : BidStatusName[BidStatus.none],
        source: window.location.href.includes(ROUTES.BUYING)
          ? BidSource.MINI_VDP
          : BidSource.VDP_OVERLAY
      });
    }
  };

  return isLoading ? (
    <StyledSummary variant={variant} data-testid={testId}>
      <Loading width="80px" height="20px" />
    </StyledSummary>
  ) : (
    <>
      {Number.isInteger(numberOfBids) && tradeState !== TradeState.upcoming && (
        <StyledSummary
          variant={variant}
          isSelling={isSelling}
          tradeState={tradeState}
          data-testid={testId}
          isCSU={isCSU}
        >
          <StyledSummaryInfoWrapper
            variant={variant === SUMMARY_VARIANT.MODAL && SUMMARY_VARIANT.TIMER}
          >
            {variant === SUMMARY_VARIANT.OVERVIEW && (
              <StyledActiveTimer variant={variant}>
                <ActiveTimer
                  onAuctionEnded={() => {}}
                  activeExpiryTime={activeExpiryTime}
                  location={TradeViewVariant.VDP}
                  tradeId={id}
                  isTimerExtended={
                    isTimerExtended ||
                    auctionSummary?.isTimerExtended ||
                    isAuctionExtended
                  }
                />
              </StyledActiveTimer>
            )}
            <BidInfoWrapper>
              <BidInfoTop>
                <TopBid
                  maxBid={auctionSummary?.maxBid}
                  topBidderSummary={auctionSummary?.topBidderSummary}
                  tradeState={tradeState}
                />
                <NumberOfBids
                  tradeState={tradeState}
                  numberOfBids={numberOfBids}
                  isSelling={isSelling}
                />
                <StyledFullHistoryLink
                  href={
                    isOmp
                      ? `${ROUTES.OMP_ENDPOINT}trade/${id}/bids`
                      : `${ROUTES.TR4_ENDPOINT}trade/${id}/bids`
                  }
                  onClick={trackViewHistory}
                  tradeState={tradeState}
                  data-testid="vdp-full-history"
                >
                  {/* Full history */}
                  <FormattedMessage
                    id={
                      variant === SUMMARY_VARIANT.PENDING && !isSelling
                        ? 'vdp.bid.fullBidHistory'
                        : 'vdp.bid.fullHistory'
                    }
                  />
                </StyledFullHistoryLink>
              </BidInfoTop>
              {(isSelling || isCSU) &&
                tradeDetails?.tradeFloorPrice?.valueDollars && (
                  <BidInfoDetails>
                    <StyledFloorPriceLabel>
                      <FormattedMessage id="vdp.floorPrice" />
                    </StyledFloorPriceLabel>
                    <StyledFloorPrice>
                      {convertToCurrency(
                        tradeDetails?.tradeFloorPrice?.valueDollars
                      )}
                    </StyledFloorPrice>
                  </BidInfoDetails>
                )}
            </BidInfoWrapper>
          </StyledSummaryInfoWrapper>
          {variant === SUMMARY_VARIANT.MODAL && (
            <StyledActiveTimer variant={SUMMARY_VARIANT.MODAL}>
              <ActiveTimer
                onAuctionEnded={() => {}}
                activeExpiryTime={activeExpiryTime}
                location={TradeViewVariant.VDP}
                tradeId={id}
                isTimerExtended={
                  isTimerExtended ||
                  auctionSummary?.isTimerExtended ||
                  isAuctionExtended
                }
              />
            </StyledActiveTimer>
          )}
        </StyledSummary>
      )}
    </>
  );
};

export default Summary;
